import React from 'react';
import PropTypes from 'prop-types';
import {Modal, DataTable} from '@/components/atoms';
import './UploadHistoryTableModal.scss';
import {MODAL_TYPE, SIZE} from '@/utils/constants/globalStyle';

/**
 * A reusable UploadHistoryTableModal that accepts:
 * - mainData: an array of row objects for the main table
 * - mainColumns: an array of column definitions for the main table
 * - subData: an array of objects for the sub-rows
 * - subColumns: an array of column definitions for the sub-table
 *
 * The table expands to show subData corresponding to each main row's "id".
 */
const UploadHistoryTableModal = ({
  openUploadHistory,
  setOpenUploadHistory,
  mainData = [],
  mainColumns = [],
  subData = [],
  subColumns = [],
  isDark = true
}) => {
  /**
   * Renders the sub-rows for an expanded row.
   * Note: we use `row.original.id` to match `parentId` in subData.
   */
  const renderSubComponent = (row) => {
    const filteredSubRows = subData.filter(
      (item) => item.parentId === row.original.id
    );
    const subComponentProps = {
      id: 'rw-upload-history-table-sub-component',
      columns: subColumns,
      data: filteredSubRows,
      isDark,
      size: SIZE.SMALL,
      tableHeader: '',
      showFilterButton: false,
      showFilterToolbar: false,
      showColumnFilters: false,
      rowSelection: {},
      showNavigation: false,
      onGlobalSearchChange: undefined,
      selection: null,
      isExpandable: false
    };
    return <DataTable {...subComponentProps} />;
  };

  /**
   * Default props for the main DataTable
   */
  const defaultProps = {
    id: 'rw-upload-history-table',
    data: mainData,
    columns: mainColumns,
    isDark,
    tableHeader: '',
    showFilterButton: false,
    showFilterToolbar: false,
    showColumnFilters: false,
    rowSelection: {},
    showNavigation: false,
    onGlobalSearchChange: undefined,
    selection: null,
    isExpandable: true,
    pageSize: 10,
    pageIndex: 0,
    renderSubComponent
  };

  return (
    <Modal
      open={openUploadHistory}
      onCloseModal={() => setOpenUploadHistory(false)}
      title='Upload History'
      showModal={openUploadHistory}
      isDark={isDark}
      type={MODAL_TYPE.LARGE}
      content={
        <div className='rw-upload-history-modal-content mt-12'>
          <DataTable {...defaultProps} />
        </div>
      }
    />
  );
};

UploadHistoryTableModal.propTypes = {
  openUploadHistory: PropTypes.bool.isRequired,
  setOpenUploadHistory: PropTypes.func.isRequired,
  mainData: PropTypes.arrayOf(PropTypes.object),
  mainColumns: PropTypes.arrayOf(PropTypes.object),
  subData: PropTypes.arrayOf(
    PropTypes.shape({
      parentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ),
  subColumns: PropTypes.arrayOf(PropTypes.object),
  isDark: PropTypes.bool
};

export default React.memo(UploadHistoryTableModal);
